import { useEffect, useRef } from "react";
import { useFocusTrap } from "../../hooks/useKeyboardNav";
import { trackEvent } from "../../lib/events";
interface CrisisModeProps {
  isActive: boolean;
  onExit: () => void;
  manual?: boolean;
  schoolCounselor?: { name: string; contact: string };
}
export function CrisisMode({
  isActive,
  onExit,
  schoolCounselor,
  manual = false,
}: CrisisModeProps) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, isActive);
  useEffect(() => {
    if (!isActive) return;
    void trackEvent({
      event_name: "crisis_resource_presented",
      payload: { resource_type: "hotline", resource_id: "support-options" },
      consent_scope: "personal_only",
    });
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onExit();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [isActive, onExit]);
  if (!isActive) return null;
  return (
    <div
      ref={ref}
      className="crisis-mode"
      role={manual ? "dialog" : "alertdialog"}
      aria-modal="true"
      aria-labelledby="crisis-title"
      tabIndex={-1}
    >
      <h1 id="crisis-title" className="crisis-mode-message">
        You do not have to handle this alone.
      </h1>
      <p>
        If there is immediate danger or a medical emergency, contact your local
        emergency service or a nearby trusted person. This app cannot provide
        emergency care.
      </p>
      <h2>United States and territories</h2>
      <p>
        These services offer free support 24/7. Phone/text service is needed
        even if the app is offline.
      </p>
      <div className="crisis-mode-actions">
        <a href="tel:988" className="crisis-call-button">
          Call 988 · Suicide &amp; Crisis Lifeline
        </a>
        <a href="sms:988" className="crisis-text-button">
          Text 988
        </a>
        <a href="sms:741741?body=HOME" className="crisis-text-button">
          Text HOME to 741741 · Crisis Text Line
        </a>
        <a
          href="https://988lifeline.org/get-help/"
          target="_blank"
          rel="noopener noreferrer"
        >
          988 chat and information (internet needed)
        </a>
        {schoolCounselor && (
          <p>
            School support: {schoolCounselor.name} · {schoolCounselor.contact}
          </p>
        )}
        <h2>Outside the United States</h2>
        <a
          href="https://findahelpline.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Find support in your country (internet needed)
        </a>
        <p>
          If disconnected, ask a trusted person to help you contact a local
          service. The US numbers above may not work in your country.
        </p>
      </div>
      <p className="text-body-xs">
        Resource links reviewed October 2026. Opening these options does not
        notify a school or label you at risk.
      </p>
      <button className="btn btn-ghost" onClick={onExit}>
        Return to what I was doing
      </button>
    </div>
  );
}
